import type { ReactElement } from 'react';
import type { ModelGender } from '@fetmode/types';
import { t } from '@fetmode/i18n';

import './GenderIcon.css';

/** Restroom-style people figures (not ♀/♂ planetary symbols). */
const GENDER_LABEL_KEYS = {
  WOMAN: 'common.home.genderWoman',
  MAN: 'common.home.genderMan',
  TRANS: 'common.home.genderTrans',
  COUPLE: 'common.home.genderCouple',
  NONBINARY: 'common.home.genderNonbinary',
} as const satisfies Record<ModelGender, string>;

/** Woman — person with skirt (classic restroom figure). */
function WomanGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      <circle cx="12" cy="4.25" r="2.75" fill="currentColor" />
      <path
        fill="currentColor"
        d="M12 8.25c-2.35 0-3.85 1.2-4.35 2.85L5.9 16.1c-.2.7.3 1.4 1.05 1.4h1.7v3.75c0 .7.55 1.25 1.25 1.25h4.2c.7 0 1.25-.55 1.25-1.25V17.5h1.7c.75 0 1.25-.7 1.05-1.4l-1.75-5c-.5-1.65-2-2.85-4.35-2.85Z"
      />
    </svg>
  );
}

/** Man — person with pants. */
function ManGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      <circle cx="12" cy="4.25" r="2.75" fill="currentColor" />
      <path
        fill="currentColor"
        d="M8.35 8.4c-.7 0-1.25.55-1.25 1.25v5.85c0 .7.55 1.25 1.25 1.25h.85v4.5c0 .7.55 1.25 1.25 1.25h3.1c.7 0 1.25-.55 1.25-1.25v-4.5h.85c.7 0 1.25-.55 1.25-1.25V9.65c0-.7-.55-1.25-1.25-1.25H8.35Z"
      />
    </svg>
  );
}

/** Trans — person figure (same body language as others; color carries meaning). */
function TransGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      <circle cx="12" cy="4.25" r="2.75" fill="currentColor" />
      <path
        fill="currentColor"
        d="M9.1 8.35c-.7 0-1.25.55-1.25 1.25v4.1c0 .45.25.85.65 1.05l1.05.55v5.95c0 .7.55 1.25 1.25 1.25h2.4c.7 0 1.25-.55 1.25-1.25v-5.95l1.05-.55c.4-.2.65-.6.65-1.05v-4.1c0-.7-.55-1.25-1.25-1.25H9.1Z"
      />
    </svg>
  );
}

/** Nonbinary — standing person (neutral stance). */
function NonbinaryGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      <circle cx="12" cy="4.25" r="2.75" fill="currentColor" />
      <path
        fill="currentColor"
        d="M8.6 8.5c-.7 0-1.25.55-1.25 1.25v4.35c0 .55.35 1.05.85 1.2l1.05.35v5.6c0 .7.55 1.25 1.25 1.25h2.9c.7 0 1.25-.55 1.25-1.25v-5.6l1.05-.35c.5-.15.85-.65.85-1.2V9.75c0-.7-.55-1.25-1.25-1.25H8.6Z"
      />
    </svg>
  );
}

/** Couple — woman + man side by side. */
function CoupleGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      {/* Woman (left) */}
      <circle cx="7.25" cy="4.1" r="2.15" fill="currentColor" />
      <path
        fill="currentColor"
        d="M7.25 7.25c-1.85 0-3 1-3.4 2.25L2.5 14.1c-.15.55.2 1.1.8 1.1h1.25v3.05c0 .55.45 1 1 1h1.4c.55 0 1-.45 1-1V15.2h1.25c.6 0 .95-.55.8-1.1L8.65 9.5c-.4-1.25-1.55-2.25-3.4-2.25Z"
      />
      {/* Man (right) */}
      <circle cx="16.75" cy="4.1" r="2.15" fill="currentColor" />
      <path
        fill="currentColor"
        d="M14.1 7.4c-.55 0-1 .45-1 1v4.4c0 .55.45 1 1 1h.55v3.45c0 .55.45 1 1 1h2.2c.55 0 1-.45 1-1V13.8h.55c.55 0 1-.45 1-1V8.4c0-.55-.45-1-1-1h-5.3Z"
      />
    </svg>
  );
}

function glyphFor(gender: ModelGender): ReactElement {
  switch (gender) {
    case 'WOMAN':
      return <WomanGlyph />;
    case 'MAN':
      return <ManGlyph />;
    case 'TRANS':
      return <TransGlyph />;
    case 'COUPLE':
      return <CoupleGlyph />;
    case 'NONBINARY':
      return <NonbinaryGlyph />;
  }
}

export interface GenderIconProps {
  gender: ModelGender | null | undefined;
  className?: string;
}

export function GenderIcon({ gender, className }: GenderIconProps): ReactElement | null {
  if (!gender) {
    return null;
  }

  const label = t(GENDER_LABEL_KEYS[gender]);
  const classes = ['gender-icon', `gender-icon--${gender.toLowerCase()}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} title={label} aria-label={label} role="img">
      {glyphFor(gender)}
    </span>
  );
}
